'use client';

import { c as _c } from "react/compiler-runtime";
import { RailCenterLabeledButton } from '@/components/layout/dashboard/app-shell-left-rail-items';
import type { AppShellLeftRailConfig, AppShellRailActionItem, AppShellRailItem } from '@/components/layout/dashboard/app-shell-left-rail.types';
import { isAppShellRailRenderItem } from '@/components/layout/dashboard/app-shell-left-rail.types';
import { AppShellRailItemTooltip } from '@/components/layout/dashboard/app-shell-rail-tooltip-content';
import { APP_SHELL_SIDEBAR_SURFACE_CLASS } from '@/components/layout/dashboard/app-shell-sidebar.constants';
import { useAppShellLeftRailConfig } from '@/components/layout/dashboard/use-app-shell-left-rail-config';
import { cn } from '@/utils/shad';
import { FC } from 'react';

/** Narrow left rail in the double-sidebar shell. */
export const APP_SHELL_LEFT_RAIL_CLASS = 'w-[56px] shrink-0';
function RailSeparator() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div aria-hidden={true} className="h-px shrink-0 bg-border" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function RailActionItem(t0) {
  const $ = _c(11);
  const {
    icon,
    label,
    onClick,
    isActive,
    isDisabled,
    allowActiveWhenDisabled,
    renderTooltip,
    dataTour
  } = t0;
  let t1;
  if ($[0] !== allowActiveWhenDisabled || $[1] !== dataTour || $[2] !== icon || $[3] !== isActive || $[4] !== isDisabled || $[5] !== label || $[6] !== onClick) {
    t1 = <RailCenterLabeledButton icon={icon} label={label} isActive={isActive} isDisabled={isDisabled} allowActiveWhenDisabled={allowActiveWhenDisabled} data-tour={dataTour} onClick={onClick} />;
    $[0] = allowActiveWhenDisabled;
    $[1] = dataTour;
    $[2] = icon;
    $[3] = isActive;
    $[4] = isDisabled;
    $[5] = label;
    $[6] = onClick;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const button = t1;
  if (!renderTooltip) {
    return button;
  }
  let t2;
  if ($[8] !== button || $[9] !== renderTooltip) {
    t2 = <AppShellRailItemTooltip renderContent={renderTooltip}>{button}</AppShellRailItemTooltip>;
    $[8] = button;
    $[9] = renderTooltip;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
function RailItem(t0) {
  const $ = _c(6);
  const {
    item
  } = t0;
  if (isAppShellRailRenderItem(item)) {
    let t1;
    if ($[0] !== item) {
      t1 = item.render();
      $[0] = item;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <>{t1}</>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== item) {
    t1 = <RailActionItem {...item} />;
    $[4] = item;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function RailSection(t0) {
  const $ = _c(8);
  const {
    items,
    className,
    dataTour
  } = t0;
  if (!items.length) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex shrink-0 flex-col items-stretch gap-1 py-2", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== items) {
    t2 = items.map(_temp);
    $[2] = items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== dataTour || $[5] !== t1 || $[6] !== t2) {
    t3 = <div className={t1} data-tour={dataTour}>{t2}</div>;
    $[4] = dataTour;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function _temp(item, index) {
  return <RailItem key={index} item={item} />;
}
type AppShellLeftRailProps = {
  config?: AppShellLeftRailConfig;
};
export const AppShellLeftRail: FC<AppShellLeftRailProps> = t0 => {
  const $ = _c(18);
  const {
    config: configProp
  } = t0;
  const defaultConfig = useAppShellLeftRailConfig();
  const config = configProp ?? defaultConfig;
  const hasCenterTop = config.centerTop.length > 0;
  const hasCenterBottom = config.centerBottom.length > 0;
  const hasCenterContent = hasCenterTop || hasCenterBottom;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(APP_SHELL_LEFT_RAIL_CLASS, APP_SHELL_SIDEBAR_SURFACE_CLASS, "flex h-full min-h-0 flex-col overflow-hidden");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== config.top) {
    t2 = <div className="shrink-0"><RailSection items={config.top} className="items-stretch py-0" /></div>;
    $[1] = config.top;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== config.centerTop) {
    t3 = <RailSection items={config.centerTop} />;
    $[3] = config.centerTop;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div aria-hidden={true} className="min-h-0 grow" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== config.centerBottom) {
    t5 = <RailSection items={config.centerBottom} />;
    $[6] = config.centerBottom;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t5) {
    t6 = <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain"><div className="flex min-h-full flex-col">{t3}{t4}{t5}</div></div>;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== config.bottom || $[12] !== hasCenterContent) {
    t7 = config.bottom.length > 0 && <div className="shrink-0">{hasCenterContent && <RailSeparator />}<RailSection items={config.bottom} className="items-center" dataTour="sidebar-settings-section" /></div>;
    $[11] = config.bottom;
    $[12] = hasCenterContent;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t2 || $[15] !== t6 || $[16] !== t7) {
    t8 = <aside aria-label="App shell left rail" className={t1}>{t2}{t6}{t7}</aside>;
    $[14] = t2;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
};